import React, { useState } from "react";
import PortoImage from "../assets/pp.jpg";
import { MdOutlineKeyboardArrowRight, MdCode, MdCloud, MdLocationOn, MdClose } from "react-icons/md";
import {Link} from "react-scroll";
import SocialLinks from "./SocialLinks";

const Home = () => {
  const [showMap, setShowMap] = useState(false);
  return (
    <div
      name="home"
      className="relative bg-[#080d14] w-full py-6 sm:py-8 md:py-10 lg:py-12 pb-12 sm:pb-16 md:pb-20 lg:pb-24 text-white overflow-hidden"
    >
      {/* Technical Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Dot grid */}
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(59,130,246,0.5) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        {/* Faint horizontal scan line */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent" />
        {/* Static corner tints — no pulse */}
        <div className="absolute top-0 left-0 w-[480px] h-[480px] bg-blue-700/6 rounded-full blur-3xl -translate-x-1/3 -translate-y-1/3 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[480px] h-[480px] bg-cyan-700/6 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none" />
      </div>

      <div className="relative max-w-screen-lg mx-auto flex flex-col items-center justify-center min-h-[calc(100vh-5rem)] px-4 sm:px-6 md:px-8 md:flex-row gap-2 sm:gap-4 md:gap-12 pt-20 md:pt-0">
        {/* Left Content */}
        <div className="flex flex-col justify-center z-10 flex-1 w-full md:w-auto text-center md:text-left">
          {/* Greeting */}
          <div className="mb-3 sm:mb-4 md:mb-5 flex items-center gap-2 sm:gap-3 justify-center md:justify-start">
            <span className="text-2xl sm:text-3xl md:text-4xl wave-animation">
              👋
            </span>
            <span className="text-blue-400 text-xs sm:text-sm md:text-base lg:text-lg font-medium tracking-wide uppercase">
              Hello, I'm
            </span>
          </div>
          
          {/* Name with Gradient */}
          <h1 className="text-4xl xs:text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold mb-3 sm:mb-4 md:mb-5 leading-[1.1] tracking-tight">
            <span className="bg-gradient-to-r from-white via-blue-200 to-cyan-300 bg-clip-text text-transparent drop-shadow-lg">
              Yemane Berhane
            </span>
          </h1>
          
          {/* Title with Icons */}
          <div className="flex items-center justify-center md:justify-start gap-2 sm:gap-3 mb-3 sm:mb-4 md:mb-5">
            <MdCode className="text-blue-400 hidden sm:block" size={24} />
            <MdCode className="text-blue-400 sm:hidden" size={20} />
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-semibold text-gray-200 tracking-tight">
              Software & DevOps Engineer
            </h2>
            <MdCloud className="text-cyan-400 hidden sm:block" size={24} />
            <MdCloud className="text-cyan-400 sm:hidden" size={20} />
          </div>
          
          {/* Location */}
          <div className="mb-4 sm:mb-6 md:mb-8">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
              <MdLocationOn className="text-purple-400" size={18} />
              <span className="text-gray-300 text-sm sm:text-base md:text-lg font-medium">
                Columbus, Ohio
              </span>
            </div>
            {/* Map Widget */}
            <div className="relative group">
              <div 
                onClick={() => setShowMap(!showMap)}
                className="cursor-pointer w-full max-w-xs mx-auto md:mx-0 h-32 sm:h-40 rounded-lg overflow-hidden border border-purple-500/30 hover:border-purple-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3055.5!2d-82.9988!3d39.9612!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x883889c1b990de71%3A0x8e919ce7c4f5fbc!2sColumbus%2C%20OH!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(20%) brightness(0.8) contrast(1.2)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Columbus, Ohio Location"
                  className="pointer-events-none"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-xs font-medium">Click to view map</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Description */}
          <p className="text-gray-300 text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 md:mb-10 max-w-lg mx-auto md:mx-0 leading-relaxed font-light tracking-wide">
            <span className="text-white font-semibold relative group">
              Senior Software Engineer
              <span className="absolute inset-0 bg-blue-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
            </span>{" "}
            building{" "}
            <span className="text-blue-400 font-semibold bg-blue-500/10 px-1.5 py-0.5 rounded hover:bg-blue-500/20 hover:text-blue-300 transition-all duration-300 cursor-default relative group">
              AI/ML systems
              <span className="absolute inset-0 bg-blue-500/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </span>,{" "}
            <span className="text-cyan-400 font-semibold bg-cyan-500/10 px-1.5 py-0.5 rounded hover:bg-cyan-500/20 hover:text-cyan-300 transition-all duration-300 cursor-default relative group">
              cloud-native infrastructure
              <span className="absolute inset-0 bg-cyan-500/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </span>, and{" "}
            <span className="text-purple-400 font-semibold bg-purple-500/10 px-1.5 py-0.5 rounded hover:bg-purple-500/20 hover:text-purple-300 transition-all duration-300 cursor-default relative group">
              automated DevOps pipelines
              <span className="absolute inset-0 bg-purple-500/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </span> that drive{" "}
            <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent font-semibold hover:from-green-300 hover:to-emerald-300 transition-all duration-300 relative group">
              reliability
              <span className="absolute inset-0 bg-green-500/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </span> and{" "}
            <span className="bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent font-semibold hover:from-orange-300 hover:to-yellow-300 transition-all duration-300 relative group">
              scalability
              <span className="absolute inset-0 bg-orange-500/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </span>.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col gap-3 sm:gap-4 justify-center md:justify-start mb-0 sm:mb-0">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center md:justify-start">
              <Link 
                to="project" 
                smooth 
                duration={500} 
                className="group relative overflow-hidden px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg text-white text-sm sm:text-base font-semibold flex items-center justify-center gap-2 hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/50"
              >
                <span>View Projects</span>
                <MdOutlineKeyboardArrowRight 
                  size={20} 
                  className="sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform duration-300"
                />
              </Link>
              <Link 
                to="contact" 
                smooth 
                duration={500} 
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gray-900/50 backdrop-blur-sm border border-gray-700/40 rounded-lg text-white text-sm sm:text-base font-semibold hover:bg-gray-800/60 hover:border-blue-500/50 transition-all duration-300 hover:scale-105"
              >
                Get In Touch
              </Link>
            </div>
            {/* Social Links - Show on small screens only */}
            <div className="flex justify-center md:hidden mt-2">
              <SocialLinks inline={true} />
            </div>
          </div>
        </div>

        {/* Right Image */}
        <div className="relative z-10 flex-shrink-0 w-full sm:w-auto">
          <div className="relative group mx-auto max-w-[280px] sm:max-w-sm md:max-w-md">
            {/* Clean border — no blur, no pulse */}
            <div className="rounded-2xl p-[2px] bg-gradient-to-br from-blue-600/60 via-cyan-500/40 to-blue-800/60 transition-all duration-300 group-hover:from-blue-500/80 group-hover:via-cyan-400/60 group-hover:to-blue-700/80">
              <div className="rounded-2xl overflow-hidden bg-gray-950">
                <img
                  src={PortoImage}
                  alt="Yemane Berhane"
                  className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>
          </div>
          

        </div>
      </div>

      {/* Full Screen Map Modal */}
      {showMap && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowMap(false)}
        >
          <div 
            className="relative w-full max-w-4xl h-[80vh] rounded-xl overflow-hidden border-2 border-purple-500/50 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowMap(false)}
              className="absolute top-4 right-4 z-10 bg-gray-900/90 hover:bg-gray-800 rounded-full p-2 text-white transition-all duration-300 hover:scale-110 shadow-lg"
              aria-label="Close map"
            >
              <MdClose size={24} />
            </button>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3055.5!2d-82.9988!3d39.9612!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x883889c1b990de71%3A0x8e919ce7c4f5fbc!2sColumbus%2C%20OH!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Columbus, Ohio - Full Map View"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;